// Routeur de la Console + garde de session. Sans session : redirection AUTOMATIQUE (SSO IdP si configuré,
// sinon login local) — l'utilisateur n'a jamais à chercher d'URL. /login reste hors garde (sinon boucle).
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { AuthProvider, useAuth, isSuperadmin } from './auth';
import { api } from './api';
import { AppShell, OrgazoneMark, RailBrand, RailFoot, ThemeVars, Topbar, alert, goToSsoLogin, signOut, useWebTheme } from '@orgazone/ui-web';
import { CONSOLE_THEME } from './theme';
import { railScopeOf } from './staff-roles';
import { ConsoleUiHost } from './ui-host';
import { TriageBell } from './topbar-bell';
import { AppLauncher } from './topbar-apps';
import { TriageProvider } from './triage-context';
import { RailLink } from './rail-link';
import { GlobalSearch } from './topbar-search';
import { Login } from './screens/Login';
import { Reset } from './screens/Reset';
import { Dashboard } from './screens/Dashboard';
import { Clients } from './screens/Clients';
import { Staff } from './screens/Staff';
import { Fleet } from './screens/Fleet';
import { Legacy } from './screens/Legacy';
import { Explorer } from './screens/Explorer';
import { Migrations } from './screens/Migrations';
import { Applications } from './screens/Applications';
import { Settings } from './screens/Settings';
import { Health } from './screens/Health';
import { Supervision } from './screens/Supervision';
import { Backups } from './screens/Backups';
import { Journal } from './screens/Journal';
import { Locations } from './screens/Locations';
import { Blocks } from './screens/Blocks';
import { Roles } from './screens/Roles';
import { Referentiels } from './screens/Referentiels';
import { Vocabulaire } from './screens/Vocabulaire';
import { GabaritsEmail } from './screens/GabaritsEmail';

function Splash({ label }: { label: string }) {
  return <div className="splash">{label}</div>;
}

/**
 * Écran d'une panne d'AUTHENTIFICATION (≠ « pas connecté »). ⚠️ Sans lui, une panne du service renvoyait
 * vers le formulaire de connexion : l'écran affirmait « vous n'êtes pas connecté » alors que la vérité
 * était « je n'ai pas pu le savoir », et l'utilisateur retapait ses identifiants contre un service HS
 * (§23.0). On affiche le message RÉEL et on propose de réessayer.
 */
function AuthDown({ message, retry }: { message: string; retry: () => void }) {
  return (
    <div className="splash">
      <div>
        <p><strong>Connexion au service impossible.</strong></p>
        <p>{message}</p>
        <button type="button" onClick={retry}>Réessayer</button>
      </div>
    </div>
  );
}

// Garde : session requise. Pas de session → SSO IdP (redirection serveur) ou login local selon le mode.
// ⚠️ Le mode vient du contexte, déjà résolu au boot : plus de second appel à /auth/mode ici (l'ancienne
// version le refetchait, avec sa propre lecture du contrat — donc une 2ᵉ décision possible sur la même
// question). Une seule résolution, un seul verdict.
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, mode, error, refresh } = useAuth();
  const nav = useNavigate();
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    if (loading || user || error || deciding) return;
    setDeciding(true);
    if (mode.oidc) goToSsoLogin(); // → IdP (repli local si IdP down) — séquence partagée
    else nav('/login', { replace: true });                       // pas de SSO → login local
  }, [loading, user, error, deciding, mode.oidc, nav]);
  // ⚠️ Réarmement : après un « Réessayer » (refresh) qui échoue encore, sans ce reset `deciding` resterait
  // true à jamais et la garde ne redéciderait plus — écran figé sur « Redirection… » sans issue.
  useEffect(() => { if (error || loading) setDeciding(false); }, [error, loading]);

  if (loading) return <Splash label="Chargement…" />;
  if (error) return <AuthDown message={error} retry={() => void refresh()} />;
  if (!user) return <Splash label="Redirection vers la connexion…" />;
  return <>{children}</>;
}

// Thème clair/sombre : mécanique PARTAGÉE `useWebTheme` (ui-web, §24.9sexies-ter d) — même clé de
// stockage qu'avant (continuité des préférences), défaut SOMBRE (identité de la Console).
function useConsoleTheme(): ['dark' | 'light', () => void] {
  const { mode, toggle } = useWebTheme('oz-console-theme', 'dark');
  return [mode, toggle];
}


// Coquille authentifiée : rail latéral + contenu (routage interne). Fondation Phase 2.

const TITLES: Record<string, string> = {
  '/': 'Tableau de bord', '/sante': 'Santé des services', '/supervision': 'Supervision', '/sauvegardes': 'Sauvegardes', '/clients': 'Clients', '/appareils': 'Appareils',
  '/locations': 'Locations', '/staff': 'Utilisateurs', '/roles': 'Rôles et droits', '/blocages': 'Blocages', '/applications': 'Applications',
  '/integrations': 'Intégrations', '/referentiels': 'Référentiels', '/vocabulaire': 'Vocabulaire', '/gabarits-email': 'Gabarits d’e-mail', '/reprise': 'Reprise de base', '/migrations': 'Migrations', '/explorateur': 'Explorateur', '/journal': 'Journal',
};

function Shell() {
  const { user } = useAuth();
  const loc = useLocation();
  // ⚠️ Correspondance par PRÉFIXE : une sous-route (`/clients/<id>`) appartient à sa section et doit
  // en garder le titre — une correspondance exacte affichait « Console » dès qu'on ouvrait une fiche.
  // Le tri par longueur décroissante fait gagner la route la PLUS spécifique.
  const pageTitle = Object.entries(TITLES)
    .sort((a, b) => b[0].length - a[0].length)
    .find(([chemin]) => loc.pathname === chemin || (chemin !== '/' && loc.pathname.startsWith(`${chemin}/`)))?.[1]
    ?? 'Console';
  // Le fil d'Ariane a cédé sa place au champ de recherche ; son information atterrit LÀ où elle sert
  // encore : l'onglet du navigateur, qui annonçait « Orgazone Console » sur les 18 écrans. Avec
  // plusieurs onglets ouverts (un client, l'explorateur, les migrations), c'est le seul repère.
  useEffect(() => { document.title = `${pageTitle} · Orgazone Console`; }, [pageTitle]);
  const sa = isSuperadmin(user);
  const [theme, toggleTheme] = useConsoleTheme();
  const scope = railScopeOf(user?.roles ?? []);
  // MODE DÉGRADÉ persistant (maquette, validé 2026-08-03) : session ouverte en BRIS DE GLACE (login
  // local alors que le SSO est configuré) → bandeau sur TOUTE la session, pas seulement au login.
  const degraded = sessionStorage.getItem('oz-degraded') === '1';
  return (
    <AppShell
      rail={<>
        {/* Marque — primitive PLATEFORME (§24.9sexies-ter a) : surtitre + nom + ligne séparatrice. */}
        <RailBrand
          logo={<OrgazoneMark size={26} />}
          surtitle="Éditeur · staff"
          name="Orgazone Console"
        />
        {/* Menu porté de la maquette (Super Admin v2) : 5 groupes. Les écrans RÉELS sont des liens ; le reste
            est « bientôt » (muet) — jamais d'écran vide simulé (§23.0). */}
        {/* ⚠️ Le rembourrage horizontal vient de l'aside (RAIL_PADDING) — le poser ici aussi l'additionnerait. */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, padding: '10px 0 0' }}>
          <RailLink to="/" end>Tableau de bord</RailLink>
          <RailLink to="/supervision" title="Santé du parc client — les périodes fiscales en attente (SA-9.2) viendront ici">Supervision</RailLink>
          <RailLink to="/sante">Santé services</RailLink>
          <RailLink to="/journal">Journal</RailLink>

          <div className="railgroup">Clients</div>
          <RailLink to="/clients">Clients</RailLink>
          <RailLink to="/appareils">Appareils</RailLink>
          <RailLink to="/locations">Locations</RailLink>
          {/* Un blocage vise un CLIENT, un magasin ou un appareil : sa place est ici, pas dans les accès. */}
          <RailLink to="/blocages">Blocages</RailLink>

          {/* ⚠️ Tout ce qui est ici s'appuie sur l'IdP : les comptes, et nos applications — leur connexion à l'IdP ET la
              sonde qui les mesure, une carte chacune (décision du 12/10). Rangées sous « Paramètres », rien ne disait
              qu'il s'agissait de l'IdP. */}
          <div className="railgroup">Identités &amp; accès</div>
          {sa && <RailLink to="/staff">Utilisateurs</RailLink>}
          {sa && <RailLink to="/applications" title="Nos applications : leur connexion par l'IdP Orgazone, et la sonde que la Console mesure pour la page Santé">Applications</RailLink>}

          <div className="railgroup">Paramètres</div>
          <RailLink to="/roles">Rôles et droits</RailLink>
          {sa && <RailLink to="/referentiels">Référentiels</RailLink>}
          {/* Le mot de chaque champ d'article par métier et par langue — un référentiel de MOTS, à côté des listes. */}
          {sa && <RailLink to="/vocabulaire" title="Le mot de chaque champ d'article, par type de commerce et par langue (« Millésime » chez le caviste)">Vocabulaire</RailLink>}
          {/* Les courriers qu'Orgazone envoie en son propre nom — un texte par usage, jamais une bibliothèque. */}
          {sa && <RailLink to="/gabarits-email" title="Le texte des courriers qu’Orgazone envoie en son nom (invitation à remplir un dossier, complément, accusé de réception)">Gabarits d’e-mail</RailLink>}
          {sa && <RailLink to="/integrations" title="Les services tiers de la plateforme : messageries, SMS, portefeuille">Intégrations</RailLink>}

          <div className="railgroup">Données</div>
          <RailLink to="/sauvegardes" title="Dépôt des dumps, réglage de la destination et de l'horaire — l'absence compte autant que le succès">Sauvegardes</RailLink>
          <RailLink to="/reprise">Reprise de base</RailLink>
          <RailLink to="/migrations">Migrations</RailLink>
          <RailLink to="/explorateur">Explorateur</RailLink>
        </nav>
        {/* Pied de rail — primitive PLATEFORME (§24.9sexies-ter k, modèle Store) : avatar + nom (email
            au survol) + portée du rôle (permissions au survol) + bouton icône Déconnexion. */}
        <RailFoot
          name={user?.name ?? user?.email ?? '—'}
          nameTitle={user?.email}
          role={scope.label}
          roleTitle={scope.tip}
          // ⚠️ DEUX TEMPS, et le second est celui qui manquait : révoquer la session LOCALE ne touche
          // pas la session SSO. Sans le passage par /auth/oidc/logout, la navigation suivante repart vers
          // l'IdP, qui reconnaît son cookie et ré-authentifie EN SILENCE — on revient sur le tableau de
          // bord en croyant s'être déconnecté. ⚠️ En cas d'échec, on NE navigue PAS et on dit pourquoi :
          // afficher une déconnexion qui n'a pas eu lieu est le pire des mensonges d'écran (§23.0).
          // ⚠️ APPEL DIRECT À L'API, surtout PAS le `logout()` du contexte : celui-ci fait
          // `setUser(null)`, ce qui réveille immédiatement la garde de route — elle constate qu'il n'y
          // a plus d'utilisateur et relance une connexion SSO. La session de l'IdP étant encore vivante
          // à cet instant, la reconnexion est SILENCIEUSE et l'utilisateur se retrouve connecté sans
          // avoir rien saisi. Constaté en journal : le POST de déconnexion était suivi 44 ms plus tard
          // d'un `/auth/oidc/login`, puis d'un callback avec un code — la redirection vers l'IdP ne
          // partait jamais. Une course perdue contre sa propre garde.
          //
          // ⚠️ Le `logout()` du contexte avale aussi les erreurs (`catch` vide) : passer par lui rendait
          // le message d'échec ci-dessous inatteignable (§23.0).
          onLogout={() => {
            sessionStorage.removeItem('oz-degraded');
            void signOut(api.logout, { onError: (message) => void alert({ title: 'Déconnexion impossible', message }) });
          }}
        />
      </>}
      header={<>
        {/* ⚠️ EN-TÊTE FIXE de la coquille : ni la barre ni le bandeau de mode dégradé ne
            défilent. Un avertissement qui s'en va au premier coup de molette n'avertit plus. */}
          {/* TOPBAR — primitive plateforme (§24.9sexies-ter b), variante VITRÉE (maquette Console).
              Cloche « À traiter » : même source que le triage du Dashboard (validé 2026-08-05). */}
          {/* ⚠️ Le fil d'Ariane « Console / <page> » a cédé la place à la RECHERCHE (décision utilisateur
              2026-08-11, parité maquette). Il n'apprenait rien : le rail marque déjà la page active et
              chaque écran répète son titre. La barre du haut sert désormais à ALLER quelque part. */}
          {/* ⚠️ PLUS DE SURCHARGE DE REMBOURRAGE (2026-09-04) : la charte aligne désormais la barre sur
              le contenu de la page, ce que cette surcharge obtenait toute seule depuis le 2026-08-11.
              Sa raison d'être est devenue la règle de tout le monde ; la garder ferait diverger la
              Console de la charte qu'elle a inspirée. */}
          {/* Lanceur « Applications » à gauche de la cloche (maquette du 2026-10-26) : l'EDI et le store par la
              connexion unique. */}
          <Topbar glass mode={theme} onToggleTheme={toggleTheme} actions={<><AppLauncher /><TriageBell /></>}>
            <GlobalSearch />
          </Topbar>
          {degraded && (
            <div style={{ background: 'var(--wa-bg)', borderBottom: '1px solid rgba(240,166,70,.35)', padding: '8px 22px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f0a646', flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--wa-fg)' }}>MODE DÉGRADÉ — session ouverte par login de secours local (bris de glace)</span>
              <span className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--wa-fg)', opacity: 0.8 }}>tracé dans idp_audit · les actions sur les identités peuvent être indisponibles</span>
            </div>
          )}
      </>}
    >
      <main className="content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/sante" element={<Health />} />
          <Route path="/supervision" element={<Supervision />} />
          <Route path="/sauvegardes" element={<Backups />} />
          <Route path="/journal" element={<Journal />} />
          {/* ⚠️ LA SÉLECTION VIT DANS L'URL, pas dans l'état de l'écran. Tant qu'elle était interne,
              cliquer « Clients » depuis une fiche ne changeait pas l'adresse : React ne re-rendait rien et
              l'utilisateur restait sur la fiche (retour utilisateur 2026-08-11). En prime, une fiche
              devient partageable, le bouton « précédent » du navigateur fonctionne, et le retour après
              reconnexion (`?next=`) ramène sur la bonne fiche au lieu de la liste. */}
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/:tenantId" element={<Clients />} />
          <Route path="/clients/:tenantId/magasin/:storeId" element={<Clients />} />
          <Route path="/appareils" element={<Fleet />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/blocages" element={<Blocks />} />
          <Route path="/reprise" element={<Legacy />} />
          <Route path="/explorateur" element={<Explorer />} />
          <Route path="/migrations" element={<Migrations />} />
          {/* ⚠️ Les écrans réservés sont gardés SUR LA ROUTE, pas seulement dans le rail : sans ça, l'URL
              tapée à la main ouvrait l'écran (le serveur refusait les mutations, mais la règle d'accès
              n'était appliquée qu'à un seul endroit — deux vérités pour un même droit). */}
          <Route path="/staff" element={sa ? <Staff /> : <Navigate to="/" replace />} />
          {/* Lecture ouverte : savoir ce qu'un rôle ouvre concerne tout le monde ; l'ÉCRITURE est refusée
              côté serveur (superadmin) et l'écran masque les actions en conséquence. */}
          <Route path="/roles" element={<Roles />} />
          <Route path="/applications" element={sa ? <Applications /> : <Navigate to="/" replace />} />
          <Route path="/integrations" element={sa ? <Settings /> : <Navigate to="/" replace />} />
          {/* L'ancienne adresse de la page (« Paramètres ») : un favori ou un lien d'aide y mène encore. */}
          <Route path="/parametres" element={<Navigate to="/integrations" replace />} />
          <Route path="/referentiels" element={sa ? <Referentiels /> : <Navigate to="/" replace />} />
          <Route path="/vocabulaire" element={sa ? <Vocabulaire /> : <Navigate to="/" replace />} />
          <Route path="/gabarits-email" element={sa ? <GabaritsEmail /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </AppShell>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* PUBLIQUE, comme /login : on y arrive par un lien d e-mail, donc sans session. Sous
              RequireAuth elle aurait renvoye au login — le jeton perdu, exactement le trou corrige. */}
          <Route path="/reset" element={<Reset />} />
          {/* Le relevé « À traiter » vit au-dessus de la coquille : la cloche et les compteurs du menu le partagent. */}
          <Route path="/*" element={<RequireAuth><TriageProvider><Shell /></TriageProvider></RequireAuth>} />
        </Routes>
        {/* Charte plateforme (contrat de thème §24.9sexies-ter) — défaut sombre ; ⚠️ divergence ÉCRITE :
            le rail de la Console reste SOMBRE dans les deux thèmes (identité staff), d'où l'override. */}
        <ThemeVars defaultMode="dark" overrides={CONSOLE_THEME} />
        {/* Hôtes uniques toasts + dialogues (§24.1quater) — remplace l'ex-ConfirmProvider local. */}
        <ConsoleUiHost />
      </AuthProvider>
    </BrowserRouter>
  );
}
